import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { VehicleSummaryRow } from '../../services/inventoryCostsApi';

type SortKey = 'margin' | 'recon_period' | 'days_on_lot' | 'listing' | 'purchase';

const fmt = (v: string | number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));

const toCsv = (rows: VehicleSummaryRow[]): string => {
  const header = [
    'Year',
    'Make',
    'Model',
    'VIN',
    'Purchase',
    'Recon (period)',
    'Recon (total)',
    'Listing',
    'Margin',
    'Days on Lot',
  ];
  const lines = [header.join(',')];
  for (const v of rows) {
    lines.push(
      [
        v.year ?? '',
        v.make ?? '',
        v.model ?? '',
        v.vin ?? '',
        v.purchase,
        v.recon_period,
        v.recon_total,
        v.listing,
        v.margin,
        v.days_on_lot ?? '',
      ]
        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
        .join(',')
    );
  }
  return lines.join('\n');
};

export const VehicleCostTable: React.FC<{ vehicles: VehicleSummaryRow[] }> = ({ vehicles }) => {
  const [sortKey, setSortKey] = useState<SortKey>('margin');
  const [asc, setAsc] = useState(false);

  const sorted = useMemo(() => {
    const arr = [...vehicles];
    arr.sort((a, b) => {
      const av = Number(a[sortKey] ?? 0);
      const bv = Number(b[sortKey] ?? 0);
      return asc ? av - bv : bv - av;
    });
    return arr;
  }, [vehicles, sortKey, asc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setAsc(!asc);
    else {
      setSortKey(key);
      setAsc(false);
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(sorted)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-costs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const headerClass = 'px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider';

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-lg font-medium">Current Fleet — {sorted.length} vehicles</h3>
        <button onClick={exportCsv} className="btn-secondary text-sm">
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={`${headerClass} text-left`}>
                Vehicle
              </th>
              <th
                scope="col"
                className={`${headerClass} text-right cursor-pointer`}
                onClick={() => toggleSort('purchase')}
              >
                Purchase
              </th>
              <th
                scope="col"
                className={`${headerClass} text-right cursor-pointer`}
                onClick={() => toggleSort('recon_period')}
              >
                Recon (period)
              </th>
              <th
                scope="col"
                className={`${headerClass} text-right cursor-pointer`}
                onClick={() => toggleSort('listing')}
              >
                Listing
              </th>
              <th
                scope="col"
                className={`${headerClass} text-right cursor-pointer`}
                onClick={() => toggleSort('margin')}
              >
                Margin
              </th>
              <th
                scope="col"
                className={`${headerClass} text-right cursor-pointer`}
                onClick={() => toggleSort('days_on_lot')}
              >
                Days on Lot
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sorted.map((v) => (
              <tr key={v.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <Link to={`/vehicles/${v.id}`} className="text-blue-600 hover:underline">
                    {v.year} {v.make} {v.model}
                  </Link>
                  <div className="text-xs text-gray-500">{v.vin}</div>
                </td>
                <td className="px-3 py-2 text-right">{fmt(v.purchase)}</td>
                <td className="px-3 py-2 text-right">{fmt(v.recon_period)}</td>
                <td className="px-3 py-2 text-right">{fmt(v.listing)}</td>
                <td className="px-3 py-2 text-right font-medium">{fmt(v.margin)}</td>
                <td className="px-3 py-2 text-right">{v.days_on_lot ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VehicleCostTable;
